// src/services/vibe.service.js
import { api } from './api';

class VibeService {
    // ============================================================
    // POSTS
    // ============================================================

    /**
     * Get vibe feed
     */
    async getFeed(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.filter) params.append('filter', filters.filter); // company, my_vibes, following, team
            if (filters.user_id) params.append('user_id', filters.user_id); // Get posts from specific user
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.offset) params.append('offset', filters.offset);
            if (filters.sortBy) params.append('sortBy', filters.sortBy); // recent, popular, trending

            const response = await api.get(`/api/vibe/feed?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching vibe feed:', error);
            throw error;
        }
    }

    /**
     * Create new post
     */
    async createPost(postData) {
        try {
            const response = await api.post('/api/vibe/posts', postData);
            return response.data;
        } catch (error) {
            console.error('Error creating vibe post:', error);
            throw error;
        }
    }

    /**
     * Update post
     */
    async updatePost(postId, postData) {
        try {
            const response = await api.put(`/api/vibe/posts/${postId}`, postData);
            return response.data;
        } catch (error) {
            console.error('Error updating vibe post:', error);
            throw error;
        }
    }

    /**
     * Delete post
     */
    async deletePost(postId) {
        try {
            const response = await api.delete(`/api/vibe/posts/${postId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting vibe post:', error);
            throw error;
        }
    }

    // ============================================================
    // REACTIONS
    // ============================================================

    /**
     * Toggle reaction on post
     */
    async toggleReaction(postId, reactionType) {
        try {
            const response = await api.post(`/api/vibe/posts/${postId}/reactions`, {
                reaction_type: reactionType
            });
            return response.data;
        } catch (error) {
            console.error('Error toggling reaction:', error);
            throw error;
        }
    }

    // ============================================================
    // COMMENTS
    // ============================================================

    /**
     * Get comments for post
     */
    async getComments(postId) {
        try {
            const response = await api.get(`/api/vibe/posts/${postId}/comments`);
            return response.data;
        } catch (error) {
            console.error('Error fetching comments:', error);
            throw error;
        }
    }

    /**
     * Create comment
     */
    async createComment(postId, commentData) {
        try {
            // Handle both string (legacy) and object (new) formats
            const payload = typeof commentData === 'string'
                ? { content: commentData }
                : commentData;

            // Temporary fix: Remove gif_url and gif_title as backend doesn't support them yet
            // Store GIF data in content as JSON string for now
            if (payload.gif_url) {
                const gifData = {
                    type: 'gif',
                    url: payload.gif_url,
                    title: payload.gif_title
                };

                // If there's already content, append GIF data
                if (payload.content) {
                    payload.content = payload.content + '\n\n' + JSON.stringify(gifData);
                } else {
                    payload.content = JSON.stringify(gifData);
                }

                // Remove the unsupported fields
                delete payload.gif_url;
                delete payload.gif_title;
            }

            console.log('📝 Sending comment payload:', payload);
            const response = await api.post(`/api/vibe/posts/${postId}/comments`, payload);
            return response.data;
        } catch (error) {
            console.error('Error creating comment:', error);
            throw error;
        }
    }

    /**
     * Delete comment
     */
    async deleteComment(commentId) {
        try {
            const response = await api.delete(`/api/vibe/comments/${commentId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting comment:', error);
            throw error;
        }
    }

    // ============================================================
    // MOOD TRACKER
    // ============================================================

    /**
     * Track today's mood
     */
    async trackMood(moodData) {
        try {
            const response = await api.post('/api/vibe/mood', moodData);
            return response.data;
        } catch (error) {
            console.error('Error tracking mood:', error);
            throw error;
        }
    }

    /**
     * Get mood analytics
     */
    async getMoodAnalytics(period = 7, scope = 'personal') {
        try {
            const response = await api.get(`/api/vibe/mood/analytics?period=${period}&scope=${scope}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching mood analytics:', error);
            throw error;
        }
    }

    // ============================================================
    // FOLLOWS
    // ============================================================

    /**
     * Follow/unfollow user
     */
    async toggleFollow(employeeId) {
        try {
            const response = await api.post(`/api/vibe/follow/${employeeId}`);
            return response.data;
        } catch (error) {
            console.error('Error toggling follow:', error);
            throw error;
        }
    }

    // ============================================================
    // SAVED POSTS
    // ============================================================

    /**
     * Save/unsave post
     */
    async toggleSavePost(postId) {
        try {
            const response = await api.post(`/api/vibe/posts/${postId}/save`);
            return response.data;
        } catch (error) {
            console.error('Error toggling save post:', error);
            throw error;
        }
    }

    // ============================================================
    // POLLS
    // ============================================================

    /**
     * Create poll
     */
    async createPoll(pollData) {
        try {
            const response = await api.post('/api/vibe/polls', pollData);
            return response.data;
        } catch (error) {
            console.error('Error creating poll:', error);
            throw error;
        }
    }

    /**
     * Get poll with user status
     */
    async getPoll(pollId) {
        try {
            const response = await api.get(`/api/vibe/polls/${pollId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching poll:', error);
            throw error;
        }
    }

    /**
     * Update poll (before ending)
     */
    async updatePoll(pollId, pollData) {
        try {
            const response = await api.put(`/api/vibe/polls/${pollId}`, pollData);
            return response.data;
        } catch (error) {
            console.error('Error updating poll:', error);
            throw error;
        }
    }

    /**
     * Vote on poll
     */
    async votePoll(pollId, pollOptionIndex) {
        try {
            const response = await api.post(`/api/vibe/polls/${pollId}/vote`, {
                poll_option_index: pollOptionIndex
            });
            return response.data;
        } catch (error) {
            console.error('Error voting on poll:', error);
            throw error;
        }
    }

    /**
     * End poll
     */
    async endPoll(pollId) {
        try {
            const response = await api.post(`/api/vibe/polls/${pollId}/end`);
            return response.data;
        } catch (error) {
            console.error('Error ending poll:', error);
            throw error;
        }
    }

    /**
     * Publish poll results
     */
    async publishPollResults(pollId) {
        try {
            const response = await api.post(`/api/vibe/polls/${pollId}/publish-results`);
            return response.data;
        } catch (error) {
            console.error('Error publishing poll results:', error);
            throw error;
        }
    }

    /**
     * Get poll results
     */
    async getPollResults(pollId) {
        try {
            const response = await api.get(`/api/vibe/polls/${pollId}/results`);
            return response.data;
        } catch (error) {
            console.error('Error fetching poll results:', error);
            throw error;
        }
    }

    // ============================================================
    // KUDOS
    // ============================================================

    /**
     * Give kudos
     */
    async giveKudos(kudosData) {
        try {
            const response = await api.post('/api/vibe/kudos', kudosData);
            return response.data;
        } catch (error) {
            console.error('Error giving kudos:', error);
            throw error;
        }
    }

    /**
     * React to kudos
     */
    async reactToKudos(kudosId, reactionType) {
        try {
            const response = await api.post(`/api/vibe/kudos/${kudosId}/reactions`, {
                reaction_type: reactionType
            });
            return response.data;
        } catch (error) {
            console.error('Error reacting to kudos:', error);
            throw error;
        }
    }

    /**
     * Get kudos leaderboard
     */
    async getKudosLeaderboard(topN = 3) {
        try {
            const response = await api.get(`/api/vibe/kudos/leaderboard?top_n=${topN}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching kudos leaderboard:', error);
            throw error;
        }
    }

    /**
     * Get recent kudos
     */
    async getRecentKudos(limit = 10) {
        try {
            const response = await api.get(`/api/vibe/kudos/recent?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching recent kudos:', error);
            throw error;
        }
    }

    // ============================================================
    // SUGGESTIONS
    // ============================================================

    /**
     * Submit suggestion
     */
    async submitSuggestion(suggestionData) {
        try {
            const response = await api.post('/api/vibe/suggestions', suggestionData);
            return response.data;
        } catch (error) {
            console.error('Error submitting suggestion:', error);
            throw error;
        }
    }

    /**
     * Get suggestions dashboard (HR/Admin)
     */
    async getSuggestionsDashboard(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.category) params.append('category', filters.category);

            const response = await api.get(`/api/vibe/suggestions/dashboard?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching suggestions dashboard:', error);
            throw error;
        }
    }

    /**
     * Respond to suggestion (HR/Admin)
     */
    async respondToSuggestion(suggestionId, responseData) {
        try {
            const response = await api.put(`/api/vibe/suggestions/${suggestionId}/respond`, responseData);
            return response.data;
        } catch (error) {
            console.error('Error responding to suggestion:', error);
            throw error;
        }
    }

    /**
     * Update suggestion status (HR/Admin)
     */
    async updateSuggestionStatus(suggestionId, newStatus) {
        try {
            const response = await api.put(`/api/vibe/suggestions/${suggestionId}/respond`, {
                suggestion_status: newStatus
            });
            return response.data;
        } catch (error) {
            console.error('Error updating suggestion status:', error);
            throw error;
        }
    }

    /**
     * Clear completed suggestions (HR/Admin)
     */
    async clearCompletedSuggestions() {
        try {
            const response = await api.delete('/api/vibe/suggestions/completed');
            return response.data;
        } catch (error) {
            console.error('Error clearing completed suggestions:', error);
            throw error;
        }
    }

    // ============================================================
    // IMAGE UPLOAD (Firebase Storage)
    // ============================================================

    /**
     * Upload image to Firebase Storage
     */
    async uploadImage(imageFile) {
        try {
            // Use Firebase Storage via upload service
            const formData = new FormData();
            formData.append('file', imageFile);
            formData.append('folder', 'vibe-images');
            formData.append('category', 'vibe-post');

            const response = await api.post('/api/upload/single', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Return format compatible with existing code
            return {
                success: true,
                data: {
                    image_url: response.data.data.downloadURL,
                    file_path: response.data.data.filePath,
                    file_name: response.data.data.fileName
                }
            };
        } catch (error) {
            console.error('Error uploading image to Firebase:', error);
            throw error;
        }
    }

    // ============================================================
    // MODERATION
    // ============================================================

    /**
     * Moderate post (Admin only)
     */
    async moderatePost(postId, moderationData) {
        try {
            const response = await api.post(`/api/vibe/posts/${postId}/moderate`, moderationData);
            return response.data;
        } catch (error) {
            console.error('Error moderating post:', error);
            throw error;
        }
    }

    /**
     * Moderate comment (Admin only)
     */
    async moderateComment(commentId, moderationData) {
        try {
            const response = await api.post(`/api/vibe/comments/${commentId}/moderate`, moderationData);
            return response.data;
        } catch (error) {
            console.error('Error moderating comment:', error);
            throw error;
        }
    }

    /**
     * Get all posts for moderation (Admin only)
     */
    async getAllPostsForModeration(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.filter) params.append('filter', filters.filter); // all, active, hidden, deleted
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.offset) params.append('offset', filters.offset);

            const response = await api.get(`/api/vibe/admin/posts?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching posts for moderation:', error);
            throw error;
        }
    }

    // ============================================================
    // SETTINGS
    // ============================================================

    /**
     * Get vibe settings
     */
    async getVibeSettings() {
        try {
            const response = await api.get('/api/vibe/settings');
            return response.data;
        } catch (error) {
            console.error('Error fetching vibe settings:', error);
            throw error;
        }
    }

    /**
     * Update vibe settings (Admin only)
     */
    async updateVibeSettings(settings) {
        try {
            const response = await api.put('/api/vibe/settings', settings);
            return response.data;
        } catch (error) {
            console.error('Error updating vibe settings:', error);
            throw error;
        }
    }

    // ============================================================
    // MENTIONS
    // ============================================================

    /**
     * Get my mentions
     */
    async getMyMentions(isRead = null) {
        try {
            const params = isRead !== null ? `?is_read=${isRead}` : '';
            const response = await api.get(`/api/vibe/mentions${params}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching mentions:', error);
            throw error;
        }
    }

    /**
     * Mark mention as read
     */
    async markMentionAsRead(mentionId) {
        try {
            const response = await api.put(`/api/vibe/mentions/${mentionId}/read`);
            return response.data;
        } catch (error) {
            console.error('Error marking mention as read:', error);
            throw error;
        }
    }

    // ============================================================
    // UTILITY METHODS
    // ============================================================

    /**
     * Extract mentions from text
     */
    extractMentions(text) {
        const mentionPattern = /@(\w+)/g;
        const mentions = [];
        let match;
        while ((match = mentionPattern.exec(text)) !== null) {
            mentions.push(match[1]);
        }
        return mentions;
    }

    /**
     * Format post type for display
     */
    getPostTypeIcon(postCategory) {
        const icons = {
            'general_vibe': '💬',
            'photo_moment': '📸',
            'suggestions': '💡',
            'kudos': '🏆',
            'poll': '📊'
        };
        return icons[postCategory] || '💬';
    }

    /**
     * Format suggestion category for display
     */
    getSuggestionCategoryLabel(category) {
        const labels = {
            'fun_idea': 'Fun Idea',
            'appreciation': 'Appreciation',
            'improvement': 'Improvement',
            'concern': 'Concern'
        };
        return labels[category] || category;
    }

    /**
     * Format suggestion status for display
     */
    getSuggestionStatusColor(status) {
        const colors = {
            'pending': 'gray',
            'reviewed': 'blue',
            'in_progress': 'yellow',
            'closed': 'green'
        };
        return colors[status] || 'gray';
    }

    // ============================================================
    // CELEBRATIONS
    // ============================================================

    /**
     * Get today's birthdays and work anniversaries
     */
    async getCelebrations() {
        try {
            const response = await api.get('/api/vibe/celebrations');
            return response.data;
        } catch (error) {
            console.error('Error fetching celebrations:', error);
            throw error;
        }
    }

    /**
     * Get upcoming birthdays (next 30 days)
     */
    async getUpcomingBirthdays(days = 30) {
        try {
            const response = await api.get('/api/vibe/upcoming-birthdays', {
                params: { days }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching upcoming birthdays:', error);
            // Fallback: return empty array if endpoint doesn't exist yet
            return { data: [] };
        }
    }

    // ============================================================
    // ANNOUNCEMENTS
    // ============================================================

    /**
     * Get company announcements
     */
    async getAnnouncements(limit = 10) {
        try {
            const response = await api.get('/api/announcements', {
                params: { limit }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching announcements:', error);
            throw error;
        }
    }

    /**
     * Create announcement (Admin only)
     */
    async createAnnouncement(announcementData) {
        try {
            const response = await api.post('/api/announcements', announcementData);
            return response.data;
        } catch (error) {
            console.error('Error creating announcement:', error);
            throw error;
        }
    }

    /**
     * Update announcement (Admin only)
     */
    async updateAnnouncement(announcementId, announcementData) {
        try {
            const response = await api.put(`/api/announcements/${announcementId}`, announcementData);
            return response.data;
        } catch (error) {
            console.error('Error updating announcement:', error);
            throw error;
        }
    }

    /**
     * Delete announcement (Admin only)
     */
    async deleteAnnouncement(announcementId) {
        try {
            const response = await api.delete(`/api/announcements/${announcementId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting announcement:', error);
            throw error;
        }
    }
}

const vibeService = new VibeService();
export default vibeService;
